import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnInit,
    OnChanges,
    SimpleChanges,
    Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';

export interface MultiSelectOption {
    id: string | number;
    name: string;
    disabled?: boolean;
}

@Component({
    selector: 'app-multi-select',
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule],
    templateUrl: './app-multiselect-control.component.html',
    styleUrls: ['./app-multiselect-control.component.css'],
})
export class MultiSelectComponent implements OnInit, OnChanges {
    @Input() options: MultiSelectOption[] = [];
    @Input() placeholder = 'Select options';

    // Behavior toggles
    @Input() emitOnClose = true; // emit when user clicks outside / presses Esc (if changed)
    @Input() emitOnChange = false; // emit immediately on every checkbox toggle

    // Icons
    @Input() defaultIcon = 'bi bi-list'; // when empty (uses bootstrap-icons if available)
    @Input() selectedIcon = 'bi bi-list-check';

    @Output() selectionChange = new EventEmitter<Array<string | number>>();

    open = false;
    searchControl = new FormControl('');
    /** internal storage = strings for robust matching */
    selected: string[] = [];

    // Use a setter so parent updates after async loads are always applied
    private _initialSelectedStrings: string[] = [];

    @Input()
    set initialSelected(vals: any) {
        // Helper to deeply flatten arrays
        const flatten = (v: any): any[] => {
            if (!Array.isArray(v)) return [v];
            return v.reduce((acc: any[], cur: any) => acc.concat(flatten(cur)), [] as any[]);
        };

        // Normalize incoming values into an array of primitives
        let items: any[] = [];
        if (vals === null || vals === undefined) {
            items = [];
        } else if (Array.isArray(vals)) {
            items = flatten(vals);
        } else {
            items = [vals];
        }

        // Turn items into strings, splitting comma-strings into multiple items
        const normalized: string[] = items
            .flatMap((it) => {
                if (it === null || it === undefined) return [];
                const s = String(it).trim();
                if (!s) return [];
                if (s.includes(','))
                    return s
                        .split(',')
                        .map((x) => x.trim())
                        .filter(Boolean);
                return [s];
            })
            .filter(Boolean);

        // dedupe while preserving order
        const seen = new Set<string>();
        const unique = normalized.filter((x) => (seen.has(x) ? false : (seen.add(x), true)));

        this._initialSelectedStrings = unique;
        // copy into internal selected so UI shows them
        this.selected = [...this._initialSelectedStrings];

        // if options exist, keep only valid ones
        if (Array.isArray(this.options) && this.selected.length) {
            const optionIdSet = new Set(this.options.map((o) => String(o.id)));
            this.selected = this.selected.filter((s) => optionIdSet.has(s));
        }

        // Keep lastEmitted aligned with new incoming value
        this._lastEmitted = this.mapSelectedBackToOriginalTypes();
    }
    get initialSelected(): Array<string | number> {
        return [...this._initialSelectedStrings];
    }

    constructor(private _eref: ElementRef) {}

    ngOnInit() {
        // defensive: if parent bound initialSelected before init, setter has already run
        if (!this.selected.length && this._initialSelectedStrings.length) {
            this.selected = [...this._initialSelectedStrings];
            this._lastEmitted = this.mapSelectedBackToOriginalTypes();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        // if options changed, prune selected values to only those that exist in options
        if (changes['options'] && Array.isArray(this.options) && this.selected.length) {
            const optionIdSet = new Set(this.options.map((o) => String(o.id)));
            const before = this.selected.slice();
            this.selected = this.selected.filter((s) => optionIdSet.has(s));
            if (this.emitOnChange && before.length !== this.selected.length) {
                this.emitIfChanged();
            }
        }
    }

    // search
    get filteredOptions() {
        const q = (this.searchControl.value || '').toString().trim().toLowerCase();
        if (!q) return this.options;
        return this.options.filter((o) => o.name.toString().toLowerCase().includes(q));
    }

    toggle() {
        this.open = !this.open;
    }
    close() {
        this.open = false;
        if (this.emitOnClose && !this.emitOnChange) {
            this.emitIfChanged();
        }
    }

    private idToStr(id: string | number) {
        return String(id);
    }

    isSelected(o: MultiSelectOption) {
        return this.selected.includes(this.idToStr(o.id));
    }

    toggleOption(o: MultiSelectOption, checked: boolean) {
        const sid = this.idToStr(o.id);
        if (checked) {
            if (!this.selected.includes(sid)) this.selected.push(sid);
        } else {
            this.selected = this.selected.filter((x) => x !== sid);
        }
        if (this.emitOnChange) this.emitIfChanged();
    }

    onRowClick(o: MultiSelectOption, _evt: MouseEvent) {
        if (o.disabled) return;
        const currently = this.isSelected(o);
        this.toggleOption(o, !currently);
    }

    selectAll() {
        const ids = this.filteredOptions.filter((x) => !x.disabled).map((x) => this.idToStr(x.id));
        const set = new Set([...this.selected, ...ids]);
        this.selected = Array.from(set);
        if (this.emitOnChange) this.emitIfChanged();
    }

    clear() {
        this.selected = [];
        if (this.emitOnChange) this.emitIfChanged();
    }

    /** Map internal string IDs back to original option id types before emitting */
    private mapSelectedBackToOriginalTypes(): Array<string | number> {
        return this.selected.map((s) => {
            const found = this.options.find((o) => this.idToStr(o.id) === s);
            return found ? found.id : s;
        });
    }

    apply() {
        // force an emit even if unchanged (user explicitly clicked Apply)
        this.emitIfChanged(true);
        this.close();
    }

    trackById(_: number, item: MultiSelectOption) {
        return item.id;
    }

    get selectedNames(): string[] {
        return this.options.filter((o) => this.selected.includes(this.idToStr(o.id))).map((o) => o.name);
    }

    get previewNames(): string[] {
        return this.selectedNames.slice(0, 2);
    }

    get extraCount(): number {
        const n = this.selectedNames.length - this.previewNames.length;
        return n > 0 ? n : 0;
    }

    // click outside closes dropdown
    @HostListener('document:click', ['$event'])
    onDocumentClick(evt: Event) {
        const clickedInside = this._eref.nativeElement.contains(evt.target);
        if (!clickedInside) this.close();
    }

    @HostListener('document:keydown.escape', ['$event'])
    onEscape(_: KeyboardEvent) {
        this.close();
    }

    // ----- emit bookkeeping -----
    // last emitted selection (original id types)
    private _lastEmitted: Array<string | number> = [];

    // order-insensitive compare to avoid spurious emits
    private arraysEqual(a: Array<string | number>, b: Array<string | number>) {
        if (a.length !== b.length) return false;
        const A = [...a].map(String).sort();
        const B = [...b].map(String).sort();
        for (let i = 0; i < A.length; i++) if (A[i] !== B[i]) return false;
        return true;
    }

    private emitIfChanged(force = false) {
        const current = this.mapSelectedBackToOriginalTypes();
        if (force || !this.arraysEqual(current, this._lastEmitted)) {
            this.selectionChange.emit(current);
            this._lastEmitted = [...current];
        }
    }
}
